import AccountService from "./AccountService.js";
import AccountSync from "./AccountSync.js";

const ID_KEY = "geonerd.playerId";
const REGISTERED_KEY = "geonerd.playerRegistered";
const SEEN_DAY_KEY = "geonerd.playerSeenDay";

/**
 * UUID v4. `crypto.randomUUID()` n'existe que dans un contexte sécurisé (HTTPS)
 * et depuis Safari 15.4 : ailleurs, il levait une exception qui interrompait
 * l'écran de fin du Défi du jour. `getRandomValues()` est disponible partout.
 */
export function randomUuid() {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
    const bytes = new Uint8Array(16);
    if (typeof crypto !== "undefined" && crypto.getRandomValues) crypto.getRandomValues(bytes);
    else for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = [...bytes].map(b => b.toString(16).padStart(2, "0")).join("");
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/** Jour calendaire UTC (`YYYY-MM-DD`), la même référence que le Défi du jour. */
function utcToday() {
    return new Date().toISOString().slice(0, 10);
}

/**
 * Compte les joueurs uniques sans compte. Un identifiant anonyme est généré
 * et envoyé au serveur une seule fois, à la première partie terminée (pas au
 * simple chargement de page, pour ne pas compter les bots/crawlers qui ne
 * jouent jamais). Les comptes créés sont déjà comptés côté serveur via la
 * table `users` — on ne les enregistre pas ici pour ne pas les compter deux fois.
 *
 * Le flag « déjà compté » n'est posé qu'après confirmation du serveur : un raté
 * réseau (ou un 5xx) laisse la clé absente, donc la prochaine partie terminée
 * réessaie, au lieu de perdre définitivement ce joueur du comptage. L'UUID, lui,
 * est persisté AVANT l'appel pour qu'un retry renvoie le même identifiant —
 * `POST /players` le dédoublonne, aucun double comptage.
 *
 * Une fois compté, l'appareil signale au plus une visite par jour (voir
 * `markActiveToday()`) : c'est ce qui mesure la rétention des joueurs sans
 * compte, sans rien envoyer d'autre que l'identifiant aléatoire.
 */
export default class PlayerIdentity {
    static _inFlight = null;

    static async registerFirstCompletion() {
        if (localStorage.getItem(REGISTERED_KEY)) return;
        // Le flag ne fait plus barrage aux appels concurrents (il n'est posé
        // qu'à la réponse) : plusieurs fins de partie enchaînées pendant la
        // requête doivent se greffer sur celle en cours, pas la relancer.
        if (this._inFlight) return this._inFlight;

        this._inFlight = this._register().finally(() => {
            this._inFlight = null;
        });
        return this._inFlight;
    }

    static async _register() {
        // No account bootstrap in flight (e.g. unit tests stubbing a bare
        // `geoNerdApp`) → login state can't be confirmed, so don't guess.
        if (!window.geoNerdApp?.accountReady) return;
        await window.geoNerdApp.accountReady;
        if (AccountSync.isLoggedIn()) return;

        const id = this.getOrCreateId();
        const { ok, status } = await AccountService.registerPlayer(id);
        // Réseau injoignable/timeout (status 0), 5xx ou 429 : transitoire, on ne
        // pose rien → nouvelle tentative à la prochaine partie terminée. Un
        // autre 4xx (identifiant refusé par le schéma) ne passera jamais :
        // inutile de le rejouer à chaque partie.
        const permanentlyRejected = status >= 400 && status < 500 && status !== 429;
        if (ok || permanentlyRejected) {
            localStorage.setItem(REGISTERED_KEY, "1");
            localStorage.setItem(SEEN_DAY_KEY, utcToday());
        }
    }

    /**
     * Identifiant aléatoire de l'appareil, créé au premier besoin. Sert aussi
     * aux scores anonymes du Défi du jour (une ligne par appareil et par jour).
     */
    static getOrCreateId() {
        let id = localStorage.getItem(ID_KEY);
        if (!id) {
            id = randomUuid();
            localStorage.setItem(ID_KEY, id);
        }
        return id;
    }

    /**
     * Visite du jour d'un appareil déjà compté, au chargement de n'importe quelle
     * page : un seul appel par jour UTC. Un appareil jamais compté (aucune partie
     * terminée) n'envoie rien, pour ne pas mesurer les robots. Même règle de
     * nouvelle tentative que l'enregistrement : un raté transitoire réessaie à la
     * page suivante.
     */
    static async markActiveToday() {
        if (!localStorage.getItem(REGISTERED_KEY)) return;
        const id = localStorage.getItem(ID_KEY);
        if (!id) return;
        const today = utcToday();
        if (localStorage.getItem(SEEN_DAY_KEY) === today) return;

        const { ok, status } = await AccountService.registerPlayer(id);
        const permanentlyRejected = status >= 400 && status < 500 && status !== 429;
        if (ok || permanentlyRejected) localStorage.setItem(SEEN_DAY_KEY, today);
    }
}
